import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Stack,
  Divider,
  InputAdornment,
  Chip,
  Tooltip,
  Switch,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExtensionIcon from '@mui/icons-material/Extension';
import StorageIcon from '@mui/icons-material/Storage';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Link as RouterLink } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AdminGate from '../components/AdminGate';
import { getApps, updateAppCosts, reorderApps, setAppVisibility } from '../services/api';

const emptyCosts = () => ({
  base: [],
  provider: { name: '', monthly: 0 },
  items: [],
});

const normalizeLine = (it) => ({
  id: it && it.id ? it.id : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  name: (it && it.name) || '',
  description: (it && it.description) || '',
  monthly: Number(it && it.monthly) || 0,
});

const normalizeCosts = (raw) => {
  const base = emptyCosts();
  if (!raw) return base;
  return {
    base: Array.isArray(raw.base) ? raw.base.map(normalizeLine) : [],
    provider: { ...base.provider, ...(raw.provider || {}) },
    items: Array.isArray(raw.items) ? raw.items.map(normalizeLine) : [],
  };
};

const cop = new Intl.NumberFormat('es-CO', {
  style: 'currency',
  currency: 'COP',
  maximumFractionDigits: 0,
});

const sumLines = (lines) => lines.reduce((s, it) => s + Number(it.monthly || 0), 0);

const appBaseTotal = (raw) => {
  const c = normalizeCosts(raw);
  return sumLines(c.base) + Number(c.provider.monthly || 0);
};

const appFullTotal = (raw) => {
  const c = normalizeCosts(raw);
  return sumLines(c.base) + Number(c.provider.monthly || 0) + sumLines(c.items);
};

const newLine = () => ({
  id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  name: '',
  description: '',
  monthly: 0,
});

const isVisible = (app) => app.visibleInCatalog !== false;

const LineChip = ({ lines, color, icon: Icon }) => {
  if (lines.length === 0) return <Typography variant="caption" color="text.disabled">—</Typography>;
  return (
    <Tooltip
      arrow
      title={
        <Box sx={{ minWidth: 180 }}>
          {lines.map((it) => (
            <Box key={it.id} sx={{ mb: 0.5, display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>{it.name}</Typography>
              <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums' }}>{cop.format(it.monthly)}</Typography>
            </Box>
          ))}
        </Box>
      }
    >
      <Chip
        icon={<Icon sx={{ fontSize: 14 }} />}
        label={lines.length}
        size="small"
        sx={{ height: 22, backgroundColor: `${color}15`, color, fontWeight: 600 }}
      />
    </Tooltip>
  );
};

const SortableRow = ({ app, onEdit, onToggleVisible }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: app.id });
  const c = normalizeCosts(app.costs);
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    backgroundColor: isDragging ? '#f6faff' : undefined,
    boxShadow: isDragging ? '0 8px 24px rgba(15,98,254,0.18)' : undefined,
    position: 'relative',
    zIndex: isDragging ? 2 : 'auto',
  };
  const visible = isVisible(app);
  const baseSum = sumLines(c.base);
  const itemsSum = sumLines(c.items);

  return (
    <TableRow ref={setNodeRef} style={style} hover sx={{ opacity: visible ? 1 : 0.55 }}>
      <TableCell sx={{ width: 36, p: 0.5, textAlign: 'center', cursor: 'grab' }} {...attributes} {...listeners}>
        <DragIndicatorIcon sx={{ color: '#9da3a8', fontSize: 18 }} />
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>{app.name}</Typography>
        {app.shortName && (
          <Typography variant="caption" color="text.secondary">{app.shortName}</Typography>
        )}
      </TableCell>
      <TableCell align="center">
        <LineChip lines={c.base} color="#0f62fe" icon={StorageIcon} />
      </TableCell>
      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{cop.format(baseSum)}</TableCell>
      <TableCell>
        {c.provider.name || <Typography variant="caption" color="text.disabled">—</Typography>}
      </TableCell>
      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{cop.format(c.provider.monthly || 0)}</TableCell>
      <TableCell align="center">
        <LineChip lines={c.items} color="#ee5396" icon={ExtensionIcon} />
      </TableCell>
      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{cop.format(appBaseTotal(app.costs))}</TableCell>
      <TableCell align="right" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{cop.format(appFullTotal(app.costs))}</TableCell>
      <TableCell align="center">
        <Tooltip title={visible ? 'Visible en /costos' : 'Oculta en /costos'} arrow>
          <Switch
            size="small"
            checked={visible}
            onChange={(e) => onToggleVisible(app.id, e.target.checked)}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': { color: '#0f62fe' },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#0f62fe' },
            }}
          />
        </Tooltip>
      </TableCell>
      <TableCell align="center">
        <IconButton size="small" onClick={() => onEdit(app)}>
          <EditIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

const LinesEditor = ({ title, helper, lines, accent, onAdd, onUpdate, onRemove }) => (
  <Box>
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
      <Box>
        <Typography variant="subtitle2">{title}</Typography>
        {helper && (
          <Typography variant="caption" color="text.secondary">{helper}</Typography>
        )}
      </Box>
      <Button onClick={onAdd} startIcon={<AddIcon />} size="small" sx={{ textTransform: 'none', color: accent }}>
        Agregar línea
      </Button>
    </Stack>
    <Stack spacing={1.5}>
      {lines.length === 0 && (
        <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderStyle: 'dashed', borderRadius: '8px' }}>
          <Typography variant="caption" color="text.secondary">
            Sin líneas. Haz clic en "Agregar línea" para crear una.
          </Typography>
        </Paper>
      )}
      {lines.map((it, idx) => (
        <Paper key={it.id} variant="outlined" sx={{ p: 2, borderRadius: '8px', borderLeft: `3px solid ${accent}` }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems="flex-start">
            <TextField
              label="Nombre"
              value={it.name}
              onChange={(e) => onUpdate(it.id, 'name', e.target.value)}
              size="small"
              sx={{ flex: 1, minWidth: 160 }}
              autoFocus={idx === lines.length - 1 && !it.name}
              placeholder="Ej: Licencia, Hosting, Soporte..."
            />
            <TextField
              label="Descripción"
              value={it.description}
              onChange={(e) => onUpdate(it.id, 'description', e.target.value)}
              size="small"
              sx={{ flex: 2, minWidth: 200 }}
              placeholder="Detalle opcional"
            />
            <TextField
              label="Costo / mes"
              value={it.monthly}
              onChange={(e) => onUpdate(it.id, 'monthly', e.target.value)}
              type="number"
              inputProps={{ min: 0, step: 1000 }}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              size="small"
              sx={{ width: { xs: '100%', md: 160 } }}
            />
            <IconButton
              size="small"
              onClick={() => onRemove(it.id)}
              sx={{ color: '#fa4d56', mt: { xs: 0, md: 0.5 } }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Paper>
      ))}
    </Stack>
  </Box>
);

const AdminCostsContent = () => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [draft, setDraft] = useState(emptyCosts());
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const fetchApps = async () => {
    try {
      setLoading(true);
      const data = await getApps();
      setApps(data);
    } catch (err) {
      setSnackbar({ open: true, message: 'Error al cargar las aplicaciones', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchApps(); }, []);

  const totals = useMemo(() => {
    const t = { base: 0, provider: 0, items: 0, grand: 0 };
    for (const a of apps) {
      const c = normalizeCosts(a.costs);
      t.base += sumLines(c.base);
      t.provider += Number(c.provider.monthly || 0);
      t.items += sumLines(c.items);
      t.grand += appFullTotal(a.costs);
    }
    return t;
  }, [apps]);

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = apps.findIndex((a) => a.id === active.id);
    const newIndex = apps.findIndex((a) => a.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(apps, oldIndex, newIndex);
    setApps(next);
    try {
      await reorderApps(next.map((a) => a.id));
    } catch (err) {
      setApps(apps);
      setSnackbar({ open: true, message: 'No se pudo guardar el orden', severity: 'error' });
    }
  };

  const handleToggleVisible = async (id, visible) => {
    const prev = apps;
    setApps((curr) => curr.map((a) => (a.id === id ? { ...a, visibleInCatalog: visible } : a)));
    try {
      await setAppVisibility(id, visible);
    } catch (err) {
      setApps(prev);
      setSnackbar({ open: true, message: 'No se pudo cambiar la visibilidad', severity: 'error' });
    }
  };

  const openEdit = (app) => {
    setEditingApp(app);
    setDraft(normalizeCosts(app.costs));
    setDialogOpen(true);
  };

  const closeEdit = () => {
    setDialogOpen(false);
    setEditingApp(null);
  };

  const setProv = (k, v) => setDraft((p) => ({ ...p, provider: { ...p.provider, [k]: v } }));

  const addBase = () => setDraft((p) => ({ ...p, base: [...p.base, newLine()] }));
  const updateBase = (id, k, v) =>
    setDraft((p) => ({ ...p, base: p.base.map((it) => (it.id === id ? { ...it, [k]: v } : it)) }));
  const removeBase = (id) => setDraft((p) => ({ ...p, base: p.base.filter((it) => it.id !== id) }));

  const addItem = () => setDraft((p) => ({ ...p, items: [...p.items, newLine()] }));
  const updateItem = (id, k, v) =>
    setDraft((p) => ({ ...p, items: p.items.map((it) => (it.id === id ? { ...it, [k]: v } : it)) }));
  const removeItem = (id) => setDraft((p) => ({ ...p, items: p.items.filter((it) => it.id !== id) }));

  const draftBaseTotal = useMemo(() => sumLines(draft.base) + Number(draft.provider.monthly || 0), [draft]);
  const draftItemsTotal = useMemo(() => sumLines(draft.items), [draft]);
  const draftTotal = draftBaseTotal + draftItemsTotal;

  const sanitizeForApi = (lines) =>
    lines
      .filter((it) => (it.name || '').trim())
      .map((it) => ({
        id: String(it.id).startsWith('tmp-') ? undefined : it.id,
        name: it.name.trim(),
        description: (it.description || '').trim(),
        monthly: Math.max(0, Number(it.monthly) || 0),
      }));

  const handleSave = async () => {
    if (!editingApp) return;
    setSaving(true);
    try {
      const payload = {
        base: sanitizeForApi(draft.base),
        provider: {
          name: (draft.provider.name || '').trim(),
          monthly: Math.max(0, Number(draft.provider.monthly) || 0),
        },
        items: sanitizeForApi(draft.items),
      };
      const updated = await updateAppCosts(editingApp.id, payload);
      setApps((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
      setSnackbar({ open: true, message: 'Costos actualizados', severity: 'success' });
      closeEdit();
    } catch (err) {
      setSnackbar({ open: true, message: 'No se pudo guardar', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const visibleCount = apps.filter(isVisible).length;

  return (
    <Box sx={{ minHeight: 'calc(100vh - 48px)', backgroundColor: '#f4f4f4', py: 4 }}>
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
          <Box>
            <Button
              component={RouterLink}
              to="/admin"
              startIcon={<ArrowBackIcon />}
              sx={{ textTransform: 'none', mb: 1 }}
            >
              Volver a Administración
            </Button>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Costos por aplicación
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Define las líneas de costo que aplican a cada herramienta (cloud, licencias, soporte, etc.). Arrastra para reordenar y usa el interruptor "Visible" para mostrar u ocultar en /costos.
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Chip label={`${apps.length} apps en total`} size="small" sx={{ height: 22, backgroundColor: '#e0e3e7', fontWeight: 500 }} />
              <Chip label={`${visibleCount} visibles en /costos`} size="small" sx={{ height: 22, backgroundColor: '#0f62fe15', color: '#0f62fe', fontWeight: 600 }} />
            </Stack>
          </Box>
        </Stack>

        <Paper sx={{ borderRadius: '12px', overflow: 'hidden' }}>
          {loading ? (
            <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f4f4f4' }}>
                    <TableCell sx={{ width: 36, p: 0.5 }} />
                    <TableCell sx={{ fontWeight: 600 }}>Aplicación</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Líneas base</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Subtotal base</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Proveedor</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Costo prov.</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Items</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Total base</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Total con items</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Visible</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Editar</TableCell>
                  </TableRow>
                </TableHead>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={apps.map((a) => a.id)} strategy={verticalListSortingStrategy}>
                    <TableBody>
                      {apps.map((app) => (
                        <SortableRow
                          key={app.id}
                          app={app}
                          onEdit={openEdit}
                          onToggleVisible={handleToggleVisible}
                        />
                      ))}
                      {apps.length > 0 && (
                        <TableRow sx={{ backgroundColor: '#0f62fe10' }}>
                          <TableCell />
                          <TableCell sx={{ fontWeight: 700 }}>Totales</TableCell>
                          <TableCell />
                          <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{cop.format(totals.base)}</TableCell>
                          <TableCell />
                          <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{cop.format(totals.provider)}</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{cop.format(totals.items)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{cop.format(totals.grand - totals.items)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, color: '#0f62fe', fontVariantNumeric: 'tabular-nums' }}>{cop.format(totals.grand)}</TableCell>
                          <TableCell />
                          <TableCell />
                        </TableRow>
                      )}
                      {apps.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                            <Typography variant="body2" color="text.secondary">No hay aplicaciones registradas todavía.</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </SortableContext>
                </DndContext>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Container>

      <Dialog open={dialogOpen} onClose={closeEdit} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Editar costos · {editingApp?.name}</span>
          <IconButton onClick={closeEdit} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <LinesEditor
            title="Costos base"
            helper="Componentes obligatorios al cotizar esta herramienta (cloud, licencias, hosting, etc.). Define solo lo que aplique."
            lines={draft.base}
            accent="#0f62fe"
            onAdd={addBase}
            onUpdate={updateBase}
            onRemove={removeBase}
          />

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Proveedor de administración</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Nombre del proveedor"
              value={draft.provider.name}
              onChange={(e) => setProv('name', e.target.value)}
              size="small"
              fullWidth
            />
            <TextField
              label="Costo mensual"
              value={draft.provider.monthly}
              onChange={(e) => setProv('monthly', e.target.value)}
              type="number"
              inputProps={{ min: 0, step: 1000 }}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              size="small"
              fullWidth
            />
          </Stack>

          <Divider sx={{ my: 3 }} />

          <LinesEditor
            title="Items adicionales"
            helper="Funcionalidades opcionales (integraciones, módulos, etc.) que el PM puede activar al cotizar."
            lines={draft.items}
            accent="#ee5396"
            onAdd={addItem}
            onUpdate={updateItem}
            onRemove={removeItem}
          />

          <Box sx={{ mt: 3, p: 2, backgroundColor: '#f4f4f4', borderRadius: '8px' }}>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">Total base ({draft.base.length} líneas + proveedor)</Typography>
              <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums' }}>{cop.format(draftBaseTotal)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">Items opcionales ({draft.items.length})</Typography>
              <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums' }}>{cop.format(draftItemsTotal)}</Typography>
            </Stack>
            <Divider sx={{ my: 1 }} />
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" sx={{ fontWeight: 600 }}>Total con todos los items</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f62fe', fontVariantNumeric: 'tabular-nums' }}>{cop.format(draftTotal)}</Typography>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit} disabled={saving} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            sx={{ textTransform: 'none', backgroundColor: '#0f62fe', '&:hover': { backgroundColor: '#0043ce' } }}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

const AdminCosts = () => (
  <AdminGate>
    <AdminCostsContent />
  </AdminGate>
);

export default AdminCosts;
